import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, History } from "lucide-react";

// Audit log will be populated from holding_regulatory_tags changes
// Empty array until tagging activity occurs

interface AuditLogEntry {
  id: string;
  date: string;
  user: string;
  holding: string;
  jurisdiction: string;
  oldTags?: string[];
  newTags?: string[];
  action: string;
  source: string;
}

export function AuditLogTab() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterUser, setFilterUser] = useState("all");
  const [filterJurisdiction, setFilterJurisdiction] = useState("all");
  const [filterSource, setFilterSource] = useState("all");

  // Empty array - will be populated from database audit trail when implemented
  const auditLog: AuditLogEntry[] = [];

  const filteredLogs = auditLog.filter((log) => {
    const matchesSearch =
      log.holding?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesUser = filterUser === "all" || log.user === filterUser;
    const matchesJurisdiction = filterJurisdiction === "all" || log.jurisdiction === filterJurisdiction;
    const matchesSource = filterSource === "all" || log.source === filterSource;

    return matchesSearch && matchesUser && matchesJurisdiction && matchesSource;
  });

  const getActionBadge = (action: string) => {
    const variants: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
      "Tags Created": "default",
      "Tags Added": "secondary",
      "Tags Updated": "outline",
      "Tags Removed": "destructive",
    };
    return <Badge variant={variants[action] || "default"}>{action}</Badge>;
  };

  const getSourceBadge = (source: string) => {
    return (
      <Badge variant={source === "agent" ? "secondary" : "outline"} className="capitalize">
        {source}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search holdings or users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={filterUser} onValueChange={setFilterUser}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by user" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterJurisdiction} onValueChange={setFilterJurisdiction}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by jurisdiction" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Jurisdictions</SelectItem>
            <SelectItem value="MAS">MAS</SelectItem>
            <SelectItem value="FATCA">FATCA</SelectItem>
            <SelectItem value="CRS">CRS</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
            <SelectItem value="agent">Agent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Audit Log Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tagging Activity Log</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredLogs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Holding</TableHead>
                  <TableHead>Jurisdiction</TableHead>
                  <TableHead>Old Tags</TableHead>
                  <TableHead>New Tags</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm text-muted-foreground font-mono">
                      {log.date}
                    </TableCell>
                    <TableCell className="font-medium">{log.user}</TableCell>
                    <TableCell>{log.holding}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-[#0033A0] text-[#0033A0]">
                        {log.jurisdiction}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {log.oldTags?.length === 0 ? (
                        <span className="text-muted-foreground text-sm italic">None</span>
                      ) : (
                        <div className="flex gap-1 flex-wrap">
                          {log.oldTags?.map((tag: string, idx: number) => (
                            <Badge key={idx} variant="secondary" className="text-xs line-through opacity-60">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.newTags?.length === 0 ? (
                        <span className="text-muted-foreground text-sm italic">None</span>
                      ) : (
                        <div className="flex gap-1 flex-wrap">
                          {log.newTags?.map((tag: string, idx: number) => (
                            <Badge key={idx} variant="default" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{getActionBadge(log.action)}</TableCell>
                    <TableCell>{getSourceBadge(log.source)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <History className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Activity Log</h3>
              <p className="text-muted-foreground max-w-sm">
                Regulatory tagging activity will be logged here as holdings are tagged and updated.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
